import { Trans } from "@lingui/react/macro"
import { toneForPhase } from "./helpers"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

/**
 * Canonical status pill for the meeting lifecycle. Wraps `<span>` with
 * `data-tone="…"` so the visual is driven by Mantine's semantic palettes
 * (success / info / warning / danger / muted) and auto-themes per product
 * via the existing resolvers.
 *
 * The component used to be re-implemented twice — once locally in
 * `-MeetingList.tsx` and once under `MeetingSummary/parts/StatusPill`. The
 * two diverged visually (different teal shades, different background alpha,
 * different default tones for `in_progress`) so the same word "Summarised"
 * read as two different pills on home vs detail. The audit on 2026-05-14
 * flagged that. Centralising here, with one canonical phase→tone map in
 * `helpers.ts`, removes the drift.
 */
export const StatusPill = ({ phase, lite = false }: IProps): React.JSX.Element => {
  const tone = toneForPhase(phase)
  return (
    <span className={classes.pill} data-tone={tone}>
      {labelFor(phase, lite)}
    </span>
  )
}

function labelFor(phase: string, lite: boolean): React.ReactNode {
  switch (phase) {
    case "summarized":
      return lite ? <Trans>Transcribed</Trans> : <Trans>Summarised</Trans>
    case "transcribed":
      return <Trans>Transcribed</Trans>
    case "parsed":
      return <Trans>Parsed</Trans>
    case "in_progress":
      return <Trans>In progress</Trans>
    case "ended":
      return <Trans>Ended</Trans>
    case "detected":
      return <Trans>Detected</Trans>
    case "fetched":
      return <Trans>Fetched</Trans>
    case "recovering":
      return <Trans>Recovering</Trans>
    case "error":
      return <Trans>Error</Trans>
    default:
      return phase.replace(/_/g, " ")
  }
}
